export type MetricKey =
  | "visitors"
  | "signIns"
  | "signInEvents"
  | "newSignups"
  | "totalSignups"
  | "cliInstalls"
  | "prReviews"
  | "mrr"
  | "activeUsers"
  | "reviewsScanned"
  | "scanRuns"
  | "installRuns"
  | "apiRequests"
  | "errorRate"
  | "revenue"
  | "payingUsers"
  | "digitalSignups"
  | "oohSignups"
  | "pageviews"
  | "blogViews";

export interface MonthlyUpdate {
  id: string;
  month: string;
  shortMonth: string;
  status: "Sent" | "Draft";
  headline: string;
  summary: string;
  focus: string;
  metrics: Partial<Record<MetricKey, number | null>>;
  highlights: string[];
  lows: string[];
  asks: string[];
  next: string[];
  product: string[];
  measurementNote?: string;
}

export const monthlyUpdates: MonthlyUpdate[] = [
  {
    id: "march-2026",
    month: "March 2026",
    shortMonth: "Mar",
    status: "Sent",
    headline: "The waitlist opened and the first demand signals appeared.",
    summary:
      "The website and waitlist went live, an offline campaign produced substantial reach, and pre-launch infrastructure activity appeared before the product reached users.",
    focus: "Turn early interest into product access and design-partner conversations.",
    metrics: {
      visitors: 265,
      signInEvents: 17,
      apiRequests: 10085,
      errorRate: 3,
      digitalSignups: 10,
      oohSignups: 18575,
      pageviews: 1465,
      blogViews: 104
    },
    highlights: [
      "The website and digital waitlist launched in March.",
      "The waitlist modal converted 26% of opens into joins.",
      "A four-hour daily offline campaign produced 18,575 signups.",
      "API activity reached 10,085 requests before the product shipped.",
      "Application exceptions fell to zero from March 27 onward."
    ],
    lows: [
      "PostHog was activated after the campaign began, so early traffic is missing.",
      "Only 10 digital waitlist signups were captured. The funnel worked, but reach to the modal was limited.",
      "Homepage calls to action produced no tracked clicks.",
      "Blog momentum faded after the middle of the month."
    ],
    asks: [
      "Help interpret the 9,136 API requests recorded across March 22 and 23.",
      "Share advice on a sustainable publishing cadence for a two-person team.",
      "Introduce devtools-focused angels and pre-seed funds.",
      "Connect us with founders who converted waitlists into design partners."
    ],
    next: [
      "Ship the first codebase scan product.",
      "Open frontend, backend agentic AI, and marketing roles.",
      "Investigate the late-March infrastructure activity.",
      "Move the website from waitlist collection to product access."
    ],
    product: [
      "GitHub App foundation",
      "Landing page and waitlist",
      "Manual codebase scan delivery",
      "Billing integration prepared"
    ],
    measurementNote:
      "March traffic is incomplete because PostHog tracking started after the offline campaign. Offline and digital signup cohorts are kept separate."
  },
  {
    id: "april-2026",
    month: "April 2026",
    shortMonth: "Apr",
    status: "Sent",
    headline: "The full internal scan path became operational.",
    summary:
      "April moved Autter from a storefront to a working product. Repository connection, branch selection, queueing, agent runs, SBOM storage, vulnerability indexing, and onboarding operated as one internal flow.",
    focus: "Prepare the internal scan system for a small external cohort.",
    metrics: {
      visitors: null,
      signIns: null,
      apiRequests: null,
      errorRate: null,
      pageviews: 628
    },
    highlights: [
      "The complete scan workflow became operational internally.",
      "Repository profiles, SBOM storage, and vulnerability indexing shipped.",
      "Slack notifications, onboarding, and webhook tests were working.",
      "AI call caching reduced inference cost.",
      "A dependency and call-graph indexer established the core technical moat."
    ],
    lows: [
      "The system was still internal and had not yet been validated by an external cohort.",
      "Most commercial metrics were not yet meaningful enough to publish.",
      "The product still needed hardening around onboarding and repeatable activation."
    ],
    asks: [
      "Introduce engineering teams willing to test an early codebase assurance workflow.",
      "Share feedback on pricing and packaging for an enforcement-first developer tool.",
      "Connect us with senior engineers who manage high-risk pull requests."
    ],
    next: [
      "Open the product to a small external cohort.",
      "Validate the code-understanding indexer on real repositories.",
      "Measure activation from repository connection to completed scan.",
      "Turn technical usage into repeatable design-partner conversations."
    ],
    product: [
      "Repository onboarding",
      "Queued agent runs",
      "Application dependency graph",
      "SBOM and vulnerability indexing",
      "Slack scan notifications"
    ],
    measurementNote:
      "Only values explicitly confirmed in the published source are shown. Unreported April metrics remain blank."
  },
  {
    id: "may-2026",
    month: "May 2026",
    shortMonth: "May",
    status: "Sent",
    headline: "Autter moved from scanning repositories to controlling merges.",
    summary:
      "The merge gate, full pull-request review pipeline, rebuilt GitHub webhook ingestion, public scan sharing, supply-chain analysis, and the first knowledge-graph indexing shipped.",
    focus: "Harden merge enforcement and convert product usage into design partnerships.",
    metrics: {
      visitors: 220,
      signInEvents: 48,
      apiRequests: 121000,
      errorRate: 0.39
    },
    highlights: [
      "Merge-blocking pull-request gates shipped.",
      "GitHub webhook ingestion was rebuilt with delivery logging.",
      "TruffleHog-backed secret scanning covered full Git history.",
      "Public scan links, branded reports, and PDF export shipped.",
      "The first knowledge-graph indexing workflow went live."
    ],
    lows: [
      "Usage attribution still mixed external behavior with internal platform activity.",
      "False-positive handling required more product work before wider enforcement.",
      "Revenue remained at zero while commercial packaging was still being tested."
    ],
    asks: [
      "Introduce teams with active GitHub repositories and meaningful pull-request volume.",
      "Connect us with application-security leaders who can pressure-test the merge gate.",
      "Share pattern recognition on moving from free technical usage to paid design partnerships."
    ],
    next: [
      "Harden merge-gate edge cases.",
      "Expand review coverage across more GitHub event types.",
      "Improve report calls to action and knowledge-graph views.",
      "Separate external usage from platform-generated activity."
    ],
    product: [
      "Merge-blocking review gate",
      "GitHub-native webhook pipeline",
      "Full-history secret scanning",
      "Public report sharing",
      "Knowledge-graph indexing"
    ],
    measurementNote:
      "May values are taken from the June update's month-over-month comparison and are rounded where the source used an approximation."
  },
  {
    id: "june-2026",
    month: "June 2026",
    shortMonth: "Jun",
    status: "Sent",
    headline: "Usage nearly doubled while reliability improved.",
    summary:
      "API activity nearly doubled, the platform error rate fell, the merge gate became an enforceable product surface, and billing and authentication moved Autter closer to commercial readiness.",
    focus: "Convert product usage into paid design partnerships.",
    metrics: {
      visitors: 429,
      signInEvents: 36,
      apiRequests: 240508,
      errorRate: 0.12
    },
    highlights: [
      "Unique visitors grew from about 220 to about 429.",
      "API requests increased from about 121,000 to 240,508.",
      "Platform error rate fell from about 0.39% to about 0.12%.",
      "Merge-blocking review gates and the full review experience were hardened.",
      "AI pull-request chat, authorship insights, and KPI digests shipped."
    ],
    lows: [
      "Sign-ins declined from 48 to 36.",
      "Revenue remained at zero.",
      "Infrastructure usage was not yet cleanly attributable to external customer adoption.",
      "Activation and false-positive reduction remained the main product constraints."
    ],
    asks: [
      "Introduce paid design partners with active GitHub repositories and real merge pain.",
      "Make warm introductions to pre-seed investors focused on developer tools, DevSecOps, security, and AI infrastructure.",
      "Connect us with senior application-security and cloud-security engineers.",
      "Help identify external sources behind the late-June increase in platform activity."
    ],
    next: [
      "Convert technical usage into paid design partnerships.",
      "Improve activation from sign-in to connected repository and first review.",
      "Separate returning users and customer activity from background platform traffic.",
      "Continue reducing false positives."
    ],
    product: [
      "Hardened merge enforcement",
      "Line-level review threads",
      "AI pull-request assistant",
      "Authorship and commit insights",
      "Weekly KPI digests"
    ],
    measurementNote:
      "API requests indicate infrastructure usage, not confirmed customer traction. External usage, returning users, background activity, repositories, pull requests, and scans need separate attribution."
  },
  {
    id: "july-2026",
    month: "July 2026",
    shortMonth: "Jul",
    status: "Sent",
    headline: "First revenue arrived while usage more than doubled.",
    summary:
      "Two users paid, active teams expanded to include KPMG, Shapoorji Pallonji, and three startups, API traffic more than doubled again, and reliability improved under the additional load.",
    focus: "Turn first revenue into a repeatable paid motion.",
    metrics: {
      visitors: 632,
      signIns: 10,
      signInEvents: 43,
      scanRuns: 1,
      apiRequests: 514360,
      errorRate: 0.03,
      revenue: 100,
      payingUsers: 2,
      pageviews: 1463,
      blogViews: 8
    },
    highlights: [
      "Autter recorded its first revenue: $100 from 2 paying users.",
      "Active teams now include KPMG, Shapoorji Pallonji, and 3 startups.",
      "API requests grew from 240,508 to 514,360, an increase of 114%.",
      "Platform error rate fell from about 0.12% to about 0.03% while load doubled.",
      "Pageviews increased from 759 to 1,463, while sign-in events rose from 36 to 43.",
      "Unique sign-in persons held at 10 while traffic nearly doubled."
    ],
    lows: [
      "First revenue is real but still small-N and founder-led.",
      "Blog views fell from 33 to 8, so content is not currently a working distribution channel.",
      "The largest July traffic day remains unattributed.",
      "Scan telemetry was barely instrumented, with only one recorded scan run."
    ],
    asks: [
      "Make warm introductions to pre-seed investors and angels focused on developer tools, DevSecOps, application security, and AI infrastructure.",
      "Introduce engineering teams with active GitHub repositories, real pull-request volume, and merge or release pain, especially teams shipping AI-generated code into production.",
      "Connect us with security, platform, and engineering leaders who can test Autter inside teams where unsafe merges are expensive.",
      "Help attribute the largest July traffic day so we can identify whether the channel is repeatable."
    ],
    next: [
      "Turn the first $100 into a repeatable paid motion.",
      "Add more paying users and move from paid users to paid teams.",
      "Instrument connected repositories and completed scans, then grow both metrics.",
      "Deepen the work with KPMG, Shapoorji Pallonji, and the three startup teams.",
      "Find the source of the largest July traffic day and try to repeat it.",
      "Keep the error rate below 0.05% as usage grows.",
      "Restart content distribution or replace blog views with a metric that means something.",
      "Keep reducing false positives so the merge gate earns trust."
    ],
    product: [
      "Merge-gate enforcement depth",
      "Scan and review flow improvements",
      "Onboarding and activation hardening",
      "Team-level usage support",
      "Reliability and error reduction"
    ],
    measurementNote:
      "Visitors and people who signed in are monthly unique people: 632 and 10 respectively. The 43 sign-in events are reported separately. Revenue and paying users are founder-confirmed rather than PostHog-derived. Scan telemetry was barely instrumented."
  },
  {
    id: "august-2026",
    month: "August 2026",
    shortMonth: "Aug",
    status: "Sent",
    headline: "Usage broke out and first revenue compounded.",
    summary:
      "Traffic, sign-ins, scans, and installs moved sharply. Revenue grew to $330.14 from 4 paying customers, while a telemetry incident made the previous error-rate metric unsuitable for August.",
    focus: "Convert the acquisition and activation spike into retained repositories and paid teams.",
    metrics: {
      visitors: 3791,
      signIns: 326,
      signInEvents: 446,
      activeUsers: 279,
      newSignups: 325,
      totalSignups: 357,
      cliInstalls: 30,
      prReviews: 221,
      mrr: 330,
      scanRuns: 102,
      installRuns: 122,
      apiRequests: 815324,
      errorRate: null,
      revenue: 330.14,
      payingUsers: 4,
      pageviews: 8660,
      blogViews: 0
    },
    highlights: [
      "Revenue grew from $100 to $330.14, while paying customers increased from 2 to 4.",
      "Pageviews rose from 1,463 to 8,660 and true monthly unique visitors increased from 632 to 3,791.",
      "Sign-in events increased from 43 to 446; unique sign-in persons moved from 10 to 326.",
      "Product usage became measurable with 102 scan runs and 122 install-script runs from a partial month of telemetry.",
      "API requests increased from 514,360 to 815,324.",
      "Two Sameboat events and X ads brought traffic; individual peak days cannot be attributed to a specific event or ad.",
      "Autter shipped 53 public changelog releases across 8 shipping days."
    ],
    lows: [
      "An August 17 panic loop dominated exception volume, so the previous API error-rate ratio is not reported for August.",
      "The Sameboat and X ads traffic spike still needs to prove conversion and retained product usage.",
      "Blog views reached zero, confirming that the old content-distribution channel is no longer useful as a KPI.",
      "Install and GitHub App event counts need cleaner definitions before they can represent retained teams.",
      "Enterprise readiness requires explicit regression coverage for organization-level data isolation and review precision."
    ],
    asks: [
      "Introduce engineering and security teams with active GitHub repositories and a costly merge or release failure mode.",
      "Connect us with observability engineers experienced in multi-tenant telemetry isolation and high-volume error pipelines.",
      "Make warm introductions to pre-seed investors focused on developer tools, application security, or AI infrastructure."
    ],
    next: [
      "Split platform exceptions from ingested Runtime telemetry and restore a denominator-matched reliability metric.",
      "Turn 122 install-script runs and 102 scan runs into retained repository usage.",
      "Grow paid teams from the current base of four paying customers.",
      "Reduce review noise and verify organization-level Runtime data isolation with regression tests.",
      "Measure conversion and retained usage from Sameboat and X ads before scaling either channel."
    ],
    product: [
      "Four-step setup and automatic first-repository scanning",
      "Runtime monitoring and external observability signals",
      "Risk-adjusted review depth",
      "Semantic pull-request search and additional policy checks",
      "One-command CLI health checks and buffered telemetry"
    ],
    measurementNote:
      "Visitors and people who signed in are monthly unique people: 3,791 and 326 respectively. The 446 sign-in events are separate. September’s comparison supplies August’s 325 new signups, 357 total signups, 279 monthly active users, 30 CLI installs, 221 PR reviews, and $330 month-end MRR. MRR is separate from $330.14 booked revenue. The 122 install-script runs were tracked from August 11 and are not comparable with CLI installs. No reliable August error-rate percentage is reported."
  },
  {
    id: "september-2026",
    month: "September 2026",
    shortMonth: "Sep",
    status: "Draft",
    headline: "More developers signed up and product activity grew.",
    summary:
      "September added 456 new signups, bringing the total to 813. Monthly active users reached 333, with 205 codebase scans and 437 PR reviews. Month-end MRR reached $495.21 from 11 paying customers. The next test is how many developers return and keep paying.",
    focus: "Measure repeat usage and conversion from signup to payment.",
    metrics: {
      visitors: null,
      signIns: null,
      activeUsers: 333,
      newSignups: 456,
      totalSignups: 813,
      cliInstalls: 94,
      scanRuns: 205,
      prReviews: 437,
      apiRequests: null,
      errorRate: null,
      revenue: null,
      mrr: 495.21,
      payingUsers: 11
    },
    highlights: [
      "New signups grew from 325 to 456, an increase of 40.3%.",
      "Total signups reached 813 at month-end, up from 357 in August.",
      "Monthly active users increased from 279 to 333, up 19.4%.",
      "PR reviews increased from 221 to 437, up 97.7%.",
      "CLI installs grew from 30 to 94, while recorded codebase scans grew from 102 to 205.",
      "Month-end MRR increased from $330 to $495.21, up 50.1%.",
      "Paying customers grew from 4 to 11, an increase of 175%."
    ],
    lows: [
      "One stronger month does not establish a steady usage baseline or retention.",
      "The paid customer base remains small; we need more teams that keep using and paying for Autter.",
      "Acquisition is not yet repeatable. We still need to identify channels that bring developers who return and pay."
    ],
    asks: [
      "Introduce engineering teams with merge or release pain, especially teams shipping AI-generated code into production.",
      "Make warm introductions to pre-seed investors focused on developer tools, application security, or AI infrastructure."
    ],
    next: [
      "Measure how many September users return and run additional scans or PR reviews.",
      "Track conversion from signup to active use and payment.",
      "Grow beyond 11 paying customers and $495.21 in month-end MRR.",
      "Close the loop on August’s error-counting and review-precision issues.",
      "Identify acquisition channels that bring developers who keep using Autter."
    ],
    product: [
      "205 recorded codebase scans",
      "437 PR reviews",
      "94 CLI installs"
    ],
    measurementNote:
      "New signups, monthly active users, and people who signed in are distinct measures. CLI installs are not directly comparable with August’s 122 install-script runs. September booked revenue, visitors, sign-ins, API requests, and error rate were not separately reported. October-inclusive signup and review totals are excluded. September month-end MRR of $495.21 and 11 paying customers are founder-confirmed. MRR is separate from booked revenue."
  }
];

export const companyThesis = {
  description:
    "Autter is the AI assurance platform for software teams. It connects code, tests, documentation, security checks, observability, and production signals to verify every change before and after release.",
  product:
    "Autter begins at the pull request. It understands what changed, maps the blast radius across the application, runs the appropriate checks, and can block an unsafe merge with evidence. Over time, the same assurance layer follows the change through testing, release, production, and fix verification.",
  whyNow:
    "AI has made code dramatically faster to produce. The systems responsible for understanding, testing, reviewing, and approving that code have not kept pace. Engineering teams are shipping larger and more frequent changes while human review capacity remains limited.",
  durableOpportunity:
    "Autter is building the assurance layer between code generation and production. Code generation models will keep changing. The need to verify what they produce is the durable infrastructure opportunity.",
  currentFocus: "Converting breakout usage into retained repositories and paid teams",
  stage: "Pre-seed",
  team: "Sagnik Ghosh and Tanvi Bhole"
};

export const assuranceWorkflow = [
  {
    title: "Build product memory",
    body: "Turn the repository into a living application graph covering services, symbols, dependencies, APIs, schemas, tests, ownership, and hotspots."
  },
  {
    title: "Understand each change",
    body: "Evaluate every pull request with codebase context, transitive blast radius, live GitHub state, and AI authorship signals."
  },
  {
    title: "Run the right verification",
    body: "Apply the security, quality, dependency, runtime, database, payment, policy, and business-logic checks relevant to that change."
  },
  {
    title: "Enforce the decision",
    body: "Block unsafe changes with evidence. Accountable overrides retain a reason and a durable audit trail."
  },
  {
    title: "Follow the change",
    body: "Track findings through release, keep documentation aligned, incorporate production signals, and verify fixes against the original failure."
  }
];

export const defensibility = [
  {
    title: "Application graph and product memory",
    body: "Autter understands how the application connects instead of treating each file as isolated text."
  },
  {
    title: "Transitive blast-radius analysis",
    body: "Graph-backed dependency and call-path analysis identifies what a change can break downstream."
  },
  {
    title: "Runtime and sandbox verification",
    body: "High-impact findings can be tested in isolated environments instead of remaining static guesses."
  },
  {
    title: "Business-logic depth",
    body: "Reviews cover authorization, state transitions, payments, migrations, concurrency, and production safety."
  },
  {
    title: "AI provenance and auditability",
    body: "Teams can trace AI-generated code and retain evidence around reviews, overrides, and model activity."
  },
  {
    title: "A compounding feedback loop",
    body: "Every finding, override, false positive, incident, and verified fix improves future assurance decisions."
  }
];

export const proofPoints = [
  "Teams will trust an AI system with merge-blocking authority when the evidence is strong and false positives are low.",
  "Autter can convert repository and CLI interest into repeatable activation and paid usage.",
  "Application context, dynamic verification, and production signals produce materially better assurance than isolated PR comments or static scanning.",
  "The initial merge-gate wedge can expand into a broader assurance control plane across the software delivery lifecycle."
];

export const founders = [
  {
    name: "Sagnik Ghosh",
    role: "Co-founder, Product and Engineering",
    bio: "Previously founded WorqHat, a developer platform used by people in more than 40 countries. He leads Autter's product, engineering, infrastructure, and technical strategy."
  },
  {
    name: "Tanvi Bhole",
    role: "Co-founder, GTM and Distribution",
    bio: "Leads positioning, marketing, outreach, partnerships, and the design-partner pipeline. Her previous work included marketing and outreach to real-estate firms, builders, and commercial partners."
  }
];

export const currentAsks = [
  {
    title: "Paid design partners",
    body: "Introductions to engineering teams with active GitHub repositories, meaningful pull-request volume, and real merge or release pain, especially teams shipping AI-generated code."
  },
  {
    title: "Investors",
    body: "Warm introductions to pre-seed investors and angels focused on developer tools, DevSecOps, application security, and AI infrastructure."
  },
  {
    title: "Security expertise",
    body: "Introductions to security, platform, and observability leaders who can test Autter inside teams where an unsafe merge is expensive and advise on multi-tenant telemetry."
  }
];
